import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import "./styles.css";
import App from "./App";
import { loadConfig } from "./config";
import { initMsal, getMsalInstance, logout as msalLogout } from "./msal";

/**
 * 0) Recover deep-link if CDN redirected to /index.html or /index.js.
 *    Runs BEFORE MSAL and React mount.
 */
(function restoreDeepLinkRobust() {
    try {
        const current = window.location;
        const pathname = current.pathname || "/";
        const search = current.search || "";
        const hash = current.hash || "";

        const appRoutes = [
            "/manage-requests",
            "/approvals",
            "/onboarding",
            "/ops",
            "/dashboard",
            "/auth/callback",
            "/unauthorized",
        ];
        const isOnAppRoute =
            appRoutes.some((prefix) => pathname.startsWith(prefix)) || pathname === "/";

        const isEntryPoint =
            pathname === "/" ||
            pathname === "/index.html" ||
            pathname === "/index.js" ||
            pathname.endsWith(".html");

        if (!isEntryPoint && isOnAppRoute) {
            return; // already fine
        }

        if (isEntryPoint && (pathname !== "/" || search || hash)) {
            const intendedPath = pathname + search + hash;
            if (
                intendedPath !== "/" &&
                intendedPath !== "/index.html" &&
                intendedPath !== "/index.js"
            ) {
                const cleanPath = intendedPath.replace(/\/index\.(html|js).*$/, "/");
                if (cleanPath !== intendedPath) {
                    window.history.replaceState(null, "", cleanPath);
                }
                return;
            }
        }

        const ref = document.referrer;
        if (ref) {
            try {
                const refUrl = new URL(ref, current.origin);
                if (refUrl.origin === current.origin) {
                    const refPath = refUrl.pathname + refUrl.search + refUrl.hash;
                    const allowed = appRoutes.some((pre) => refUrl.pathname.startsWith(pre));
                    if (allowed && refPath !== "/" && !refPath.includes("index.")) {
                        window.location.replace(refPath);
                        return;
                    }
                }
            } catch {
                /* ignore */
            }
        }
    } catch (e) {
        console.warn("Deep link restore failed", e);
    }
})();

/**
 * 1) Normal app bootstrap
 */
async function start() {
    // Load runtime config with a visible failure mode if something goes wrong in DEV
    try {
        await loadConfig();
    } catch (e) {
        console.error("Failed to load runtime config:", e);
        const el = document.getElementById("root");
        if (el) {
            el.innerHTML = `
                <div style="font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; padding: 24px; line-height:1.5;">
                  <h2 style="margin:0 0 12px;">Configuration Load Error</h2>
                  <p style="margin:0 0 12px;">${String(e?.message || e)}</p>
                  <ul style="margin:0 0 12px 20px;">
                    <li>Verify <code>/config.json</code> exists at the site root (same level as <code>index.html</code>).</li>
                    <li>Ensure your CDN/static host does <strong>not</strong> rewrite <code>/config.json</code> to <code>/index.html</code>.</li>
                    <li>Confirm the response has valid JSON and a correct <code>Content-Type: application/json</code>.</li>
                  </ul>
                  <p style="margin:0;">Tip: <code>curl -I https://your-domain/config.json</code> should return 200 and JSON content type.</p>
                </div>
            `;
        }
        return; // stop bootstrapping here
    }

    await initMsal(); // create PCA + handle redirect
    const pca = getMsalInstance();

    window.addEventListener("tb:logout", () => {
        msalLogout().catch((e) => console.error("[MSAL] logout error", e));
    });

    const root = createRoot(document.getElementById("root"));
    root.render(
        <React.StrictMode>
            <MsalProvider instance={pca}>
                <BrowserRouter>
                    <App />
                </BrowserRouter>
            </MsalProvider>
        </React.StrictMode>
    );
}

start().catch((e) => {
    console.error("Bootstrap failed", e);
    const el = document.getElementById("root");
    if (el) {
        el.innerHTML = `
            <div style="font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; padding: 24px; line-height:1.5;">
              <h2 style="margin:0 0 12px;">Bootstrap Error</h2>
              <p style="margin:0;">${String(e?.message || e)}</p>
            </div>
        `;
    }
});
